import { AlertCircle, AlertTriangle, Info, Lightbulb } from "lucide-react";
import { useProjectLspState } from "../lib/lsp/useProjectLsp.ts";
import type { ProblemItem } from "../lib/lsp/diagnosticsStore.ts";

const ICONS = { 1: AlertCircle, 2: AlertTriangle, 3: Info, 4: Lightbulb } as const;
const LABEL = { 1: "Error", 2: "Warning", 3: "Info", 4: "Hint" } as const;

export function problemCounts(problems: readonly ProblemItem[]): { errors: number; warnings: number } {
  let errors = 0;
  let warnings = 0;
  for (const p of problems) {
    if (p.severity === 1) errors++;
    else if (p.severity === 2) warnings++;
  }
  return { errors, warnings };
}

/** Project-scoped diagnostics from the Agent-side language server (the same source as the inline Monaco markers). */
export function ProblemsPanel() {
  const lsp = useProjectLspState();
  const { problems, state, message } = lsp;
  return (
    <div className="problems" data-testid="problems" data-lsp-state={state}>
      {state === "unavailable" && (
        <div className="muted problems-note" data-testid="lsp-unavailable">
          {message ?? "No language server available"}
        </div>
      )}
      {state === "crashed" && (
        <div className="error banner problems-note" data-testid="lsp-crashed">
          The language server stopped{message ? `: ${message}` : ""}.{" "}
          <button onClick={lsp.restart} data-testid="lsp-restart">
            Restart
          </button>
        </div>
      )}
      {(state === "idle" || state === "starting") && problems.length === 0 && (
        <div className="muted problems-note">
          {state === "starting" ? "Starting language server…" : "Open a TypeScript or JavaScript file to see problems."}
        </div>
      )}
      {state === "ready" && problems.length === 0 && (
        <div className="muted problems-note" data-testid="problems-empty">
          No problems detected.
        </div>
      )}
      <ul className="problems-list">
        {problems.map((p, i) => {
          const Icon = ICONS[p.severity];
          return (
            <li key={`${p.path}:${p.line}:${p.column}:${i}`}>
              <button
                className={`problem sev-${p.severity}`}
                onClick={() => lsp.reveal(p)}
                data-testid="problem"
                data-path={p.path}
                data-line={p.line}
                data-severity={LABEL[p.severity]}
                title={`${LABEL[p.severity]}: ${p.message}`}
              >
                <Icon size={13} aria-hidden />
                <span className="problem-msg">{p.message}</span>
                <span className="muted problem-src">
                  {p.source ?? ""}
                  {p.code ? ` (${p.code})` : ""}
                </span>
                <span className="muted problem-loc">
                  {p.path} [{p.line}, {p.column}]
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
